import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap } from "lucide-react";
import AISearchMockup from "./AISearchMockup";
import { useShouldReduceAnimations } from "@/hooks/useReducedMotion";

const Hero = () => {
  const reduceAnimations = useShouldReduceAnimations();
  
  return (
    <section className="relative flex items-center pt-20 pb-8 md:pt-24 md:pb-12 overflow-hidden">
      {/* Background effects - using transform for positioning to prevent CLS */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
      <div 
        className="absolute w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" 
        style={{ top: '25%', left: '25%', transform: 'translate3d(0, 0, 0)' }}
        aria-hidden="true"
      />
      <div 
        className="absolute w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" 
        style={{ bottom: '25%', right: '25%', transform: 'translate3d(0, 0, 0)' }}
        aria-hidden="true"
      />
      
      <div className="container mx-auto px-4 max-w-[1280px]">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left content */}
          <motion.div
            initial={reduceAnimations ? { opacity: 1 } : { opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: reduceAnimations ? 0 : 0.4 }}
            className="space-y-5 md:space-y-6 text-center lg:text-left"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-foreground uppercase tracking-wider text-xs font-semibold">First-Mover Advantage in AI Advertising</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Get Your Business Found{" "}
              <span className="text-gradient">Where AI Searches First</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0">
              Your customers are asking ChatGPT instead of Googling. Be the business they find. 
              We run ads on AI platforms to generate leads for local service businesses.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start relative z-10">
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-all text-base px-8"
                asChild
              >
                <a href="#contact">
                  GET A FREE AI ADS AUDIT
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-border hover:bg-secondary hover:border-primary/50 hover:text-primary text-base transition-all"
                asChild
              >
                <a href="#how-it-works">SEE HOW IT WORKS</a>
              </Button>
            </div>

            <div className="flex items-center gap-6 justify-center lg:justify-start text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span>100% Automated</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span>Leads on Autopilot</span>
              </div>
            </div>
          </motion.div>

          {/* Right content - AI Mockup */}
          <motion.div
            initial={reduceAnimations ? { opacity: 1 } : { opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: reduceAnimations ? 0 : 0.4, delay: reduceAnimations ? 0 : 0.1 }}
            className="relative"
          >
            <AISearchMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
